import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, ExternalLink, Star, Share2, Truck, PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';
import { toast } from 'sonner';
import axios from 'axios';
import WishlistButton from '@/components/WishlistButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductCard = ({ product }) => {
  const { addToCart, loading } = useCart();
  const { formatPrice, formatShipping, countryInfo } = useCurrency();
  const isPhysical = product.product_type === 'physical';
  const [trackingClick, setTrackingClick] = useState(false);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const result = await addToCart(product.id);
    if (result.success) {
      toast.success(`${product.name} added to cart`);
    } else {
      toast.error(result.error);
    }
  };

  const handleAffiliateClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setTrackingClick(true);
    try {
      // Track click and get affiliate URL with tracking params
      const response = await axios.post(`${API}/affiliate-tracking/track-click`, {
        product_id: product.id
      });
      
      const { affiliate_url } = response.data;
      
      // Open the tracked affiliate URL
      window.open(affiliate_url, '_blank');
    } catch (error) {
      console.error('Failed to track click:', error);
      // Fallback to direct URL if tracking fails
      window.open(product.affiliate_url, '_blank');
    } finally {
      setTrackingClick(false);
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const shareUrl = `${window.location.origin}/products/${product.id}`;
    const shareData = {
      title: product.name,
      text: `Check out ${product.name} - ${formatPrice(product.price)} on SALESSPACE!`,
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        toast.success('Shared successfully!');
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    }
  };

  return (
    <div
      className={`group relative bg-card rounded-md border transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 overflow-hidden ${
        isPhysical ? 'product-card-physical' : 'product-card-affiliate'
      }`}
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/products/${product.id}`} className="block">
        {/* Image Container */}
        <div className="relative aspect-square overflow-hidden bg-muted">
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
          {product.videos?.length > 0 && (
            <div className="absolute bottom-3 left-3">
              <Badge className="bg-black/70 text-white gap-1" data-testid={`video-badge-${product.id}`}>
                <PlayCircle className="w-3 h-3" />
                Video
              </Badge>
            </div>
          )}
          {product.featured && (
            <div className="absolute top-3 left-3">
              <Badge className="bg-foreground text-background gap-1">
                <Star className="w-3 h-3 fill-current" />
                Featured
              </Badge>
            </div>
          )}
          <div className="absolute top-3 right-3 flex gap-2">
            <WishlistButton product={product} className="h-7 w-7 bg-white/90 hover:bg-white" />
            <Button
              size="icon"
              variant="secondary"
              className="h-7 w-7 bg-white/90 hover:bg-white"
              onClick={handleShare}
              data-testid={`share-product-${product.id}`}
            >
              <Share2 className="w-3.5 h-3.5 text-gray-700" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="mb-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {product.category}
            </span>
          </div>
          <h3 className="font-semibold text-base mb-2 line-clamp-2 group-hover:text-foreground/80 transition-colors" style={{ fontFamily: 'Archivo, sans-serif' }}>
            {product.name}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
            {product.description}
          </p>

          {/* Price in Local Currency */}
          <div className="flex items-center justify-between mb-2">
            <div>
              <span className="text-lg font-bold text-foreground">
                {formatPrice(product.price)}
              </span>
              {isPhysical && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                  <Truck className="w-3 h-3" />
                  <span>+{formatShipping()} shipping to {countryInfo.flag}</span>
                </div>
              )}
            </div>
            
            {isPhysical ? (
              <Button
                size="sm"
                onClick={handleAddToCart}
                disabled={loading}
                className="gap-2 bg-[#2563EB] hover:bg-[#2563EB]/90"
                data-testid={`add-to-cart-${product.id}`}
              >
                <ShoppingCart className="w-4 h-4" />
                Add
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={handleAffiliateClick}
                disabled={trackingClick}
                className="gap-2 border-[#F97316] text-[#F97316] hover:bg-[#F97316]/10"
                data-testid={`affiliate-link-${product.id}`}
              >
                <ExternalLink className="w-4 h-4" />
                {trackingClick ? '...' : 'Get Deal'}
              </Button>
            )}
          </div>

          {/* Commission info removed - only visible in Admin dashboard */}
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;
